import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, Clock, ArrowRight, Search, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then(r => {
      setPosts(r.data.posts || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const featured = posts.filter(p => p.featured);
  const filtered = searchTerm ? posts.filter(p => p.title.toLowerCase().includes(searchTerm.toLowerCase()) || p.category.toLowerCase().includes(searchTerm.toLowerCase())) : posts;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/jobs" className="text-sm text-[#1C2A24]/70 hover:text-[#047857]">Find Jobs</Link>
            <Link to="/auth"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-9 px-5 text-sm" data-testid="blog-signup">Sign Up Free</Button></Link>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        {!selected ? (
          <>
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>WFH Resources & Guides</h1>
              <p className="text-[#1C2A24]/60 mt-2">Expert tips, job listings, and everything you need to succeed working from home</p>
            </div>

            {/* Search */}
            <div className="relative mb-8">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search articles..."
                className="w-full pl-10 h-12 rounded-full bg-white border border-[#E5E0D8] text-sm focus:outline-none focus:ring-2 focus:ring-[#047857]" data-testid="blog-search" />
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
              </div>
            ) : (
              <>
                {/* Featured */}
                {!searchTerm && featured.length > 0 && (
                  <div className="mb-10" data-testid="featured-posts">
                    <h2 className="text-lg font-medium mb-4 flex items-center gap-2" style={{fontFamily:'Outfit'}}><TrendingUp size={16} className="text-[#047857]" /> Featured Articles</h2>
                    <div className="grid md:grid-cols-3 gap-4">
                      {featured.map(post => (
                        <div key={post.id} onClick={() => setSelected(post)}
                          className="bg-white rounded-3xl border border-[#E5E0D8] p-6 cursor-pointer hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300" data-testid={`post-${post.id}`}>
                          <span className="text-[10px] bg-[#A7F3D0]/30 text-[#047857] px-2 py-0.5 rounded-full">{post.category}</span>
                          <h3 className="text-base font-medium mt-3 mb-2 line-clamp-2" style={{fontFamily:'Outfit'}}>{post.title}</h3>
                          <p className="text-xs text-[#1C2A24]/50 line-clamp-2">{post.excerpt}</p>
                          <div className="flex items-center gap-2 mt-3 text-xs text-[#1C2A24]/40">
                            <Clock size={10} strokeWidth={1.5} />{post.read_time}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Posts */}
                <div data-testid="all-posts">
                  <h2 className="text-lg font-medium mb-4" style={{fontFamily:'Outfit'}}>{searchTerm ? 'Search Results' : 'All Articles'}</h2>
                  {filtered.length === 0 ? (
                    <p className="text-sm text-[#1C2A24]/40 py-8 text-center">No articles found.</p>
                  ) : (
                    <div className="space-y-3">
                      {filtered.map(post => (
                        <div key={post.id} onClick={() => setSelected(post)}
                          className="bg-white rounded-2xl border border-[#E5E0D8] p-5 cursor-pointer hover:border-[#047857]/30 transition-all flex items-center gap-4" data-testid={`post-row-${post.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] bg-[#F4F1E8] text-[#1C2A24]/60 px-2 py-0.5 rounded-full">{post.category}</span>
                              <span className="text-[10px] text-[#1C2A24]/30"><Clock size={8} className="inline mr-1" />{post.read_time}</span>
                            </div>
                            <h3 className="font-medium text-sm truncate" style={{fontFamily:'Outfit'}}>{post.title}</h3>
                            <p className="text-xs text-[#1C2A24]/50 mt-1 line-clamp-1">{post.excerpt}</p>
                          </div>
                          <ArrowRight size={16} className="text-[#E5E0D8] shrink-0" strokeWidth={1.5} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          /* Article View */
          <div data-testid="article-view">
            <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1 text-sm text-[#047857] mb-6 hover:opacity-80"><ArrowLeft size={14} strokeWidth={1.5} /> Back to articles</button>
            <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <span className="text-xs bg-[#A7F3D0]/30 text-[#047857] px-3 py-1 rounded-full">{selected.category}</span>
              <h1 className="text-2xl md:text-3xl font-medium mt-4 mb-2" style={{fontFamily:'Outfit'}}>{selected.title}</h1>
              <p className="text-sm text-[#1C2A24]/50 mb-8 flex items-center gap-2"><Clock size={12} />{selected.read_time} read</p>
              <div className="prose prose-sm max-w-none">
                {selected.content.split('\n').map((line, i) => {
                  if (line.startsWith('**') && line.endsWith('**')) return <h3 key={i} className="text-lg font-medium mt-6 mb-2" style={{fontFamily:'Outfit'}}>{line.replace(/\*\*/g, '')}</h3>;
                  if (line.startsWith('**')) return <p key={i} className="text-[#1C2A24]/80 leading-relaxed mb-2"><strong>{line.replace(/\*\*/g, '')}</strong></p>;
                  if (line.startsWith('- ') || line.startsWith('1.')) return <p key={i} className="text-[#1C2A24]/70 leading-relaxed ml-4 mb-1">{line}</p>;
                  if (line.trim() === '') return <br key={i} />;
                  return <p key={i} className="text-[#1C2A24]/70 leading-relaxed mb-2">{line}</p>;
                })}
              </div>
              <div className="mt-8 pt-6 border-t border-[#E5E0D8] flex gap-3">
                <Link to="/jobs"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white">Browse Jobs</Button></Link>
                <Link to="/tools"><Button variant="outline" className="rounded-full border-[#E5E0D8]">Try WFH Tools</Button></Link>
                <Link to="/auth"><Button variant="outline" className="rounded-full border-[#E5E0D8]">Sign Up Free</Button></Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
